import PropTypes from 'prop-types';
import './TextArea.styles.css';
import React, { useEffect, useRef, useImperativeHandle } from 'react';

const TextArea = React.forwardRef(({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  error,
  disabled,
  className = '',
  onKeyDown,
  inputClassName = '',
  required,
}, ref) => {
  const textareaRef = useRef(null);

  // Expose focus method through the ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current.focus();
    }
  }));

  // Auto-resize logic
  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Adjust on mount & when value changes
  useEffect(() => {
    adjustHeight();
  }, [value]);

  return (
    <div className={`flex flex-col w-full ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${className}`}>
      {label && (
        <label className={`block text-[0.7rem] font-medium ${disabled ? 'text-gray-400' : 'text-gray-800'}`}>
          {label}
          {required && <span className="text-brand-error ml-0.5 font-normal">*</span>}
        </label>
      )}
      <div className="relative w-full">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          className={`textarea-input ${inputClassName}`}
          onKeyDown={onKeyDown}
        />
      </div>
      {error && typeof error === 'string' && (
        <span className="textarea-error">{error}</span>
      )}
    </div>
  );
});

TextArea.displayName = 'TextArea';

TextArea.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onKeyDown: PropTypes.func,
  inputClassName: PropTypes.string,
  required: PropTypes.bool,
};

export default TextArea; 